/**
 * HBOL import: a forwarder's pre-alert for one LCL container in, Master Cargo's
 * house-bill import template (CSV/XLSX) out. See backend/apps/hbol.
 */
import { queryOptions } from "@tanstack/react-query";

import { api, API_URL } from "./api";
import type { Paginated } from "./users";

export type HbolStatus = "draft" | "reading" | "ready" | "failed";

export const HBOL_STATUS_LABEL: Record<HbolStatus, string> = {
  draft: "Draft",
  reading: "Reading",
  ready: "Ready",
  failed: "Failed",
};

export type PageKind = "manifest" | "house_bl" | "master_bl" | "invoice" | "other";

export const PAGE_KIND_LABEL: Record<PageKind, string> = {
  manifest: "Manifest",
  house_bl: "House bill",
  master_bl: "Master bill",
  invoice: "Invoice",
  other: "Other",
};

export interface HbolPage {
  page: number;
  kind: PageKind;
  /** Clockwise turn relative to the page as displayed. */
  rotation: number;
  /** Absolute rotation that shows the page upright (includes the turn stored in the PDF). */
  upright?: number;
  house_bl: string;
  /** The page numbering printed on the page ("Page 3 of 8"); repeats mark a second copy. */
  page_label?: string;
  reason: string;
}

export interface HbolFile {
  id: string;
  filename: string;
  source_archive: string;
  page_count: number;
  position: number;
  /** Empty until the reader sorted the pages. */
  pages: HbolPage[];
  url: string;
  uploaded_by_email: string;
  created_at: string;
}

export interface HbolWarning {
  code: string;
  message: string;
}

export type RowSource = "manifest" | "house_bl" | "both" | "manual";

export const ROW_SOURCE_LABEL: Record<RowSource, string> = {
  manifest: "From the manifest",
  house_bl: "From the house bill",
  both: "Manifest and house bill agree on the number",
  manual: "Typed by hand",
};

/** One house bill: one row of the import template, columns in template order. */
export interface HbolRow {
  id: string;
  position: number;
  house_bl: string;
  consignee: string;
  document_number: string;
  description: string;
  quantity: number | null;
  weight: string | null;
  weight_unit: string;
  volume: string | null;
  volume_unit: string;
  source: RowSource;
  source_file: string | null;
  source_page: number | null;
  warnings: HbolWarning[];
  /** Changed by a person; reading again leaves it alone. */
  edited: boolean;
}

export type RowPatch = Partial<
  Pick<
    HbolRow,
    | "house_bl"
    | "consignee"
    | "document_number"
    | "description"
    | "quantity"
    | "weight"
    | "weight_unit"
    | "volume"
    | "volume_unit"
  >
>;

export interface Quantities {
  pieces: number | null;
  package_kind: string;
  weight: number | null;
  weight_unit: string;
  volume: number | null;
  volume_unit: string;
}

export interface HbolExtracted {
  container?: string;
  master_bl?: string;
  forwarder?: string;
  vessel_voyage?: string;
  manifest_totals?: Quantities | null;
  warnings?: HbolWarning[];
  pages?: Partial<Record<PageKind, number>>;
  read?: { manifests: number; house_bills: number };
}

export interface HbolTotals {
  rows: number;
  quantity: number;
  /** Sums per unit, e.g. {"KG": "4095.180"}; units are never mixed in one sum. */
  weight: Record<string, string>;
  volume: Record<string, string>;
}

export interface HbolJobListItem {
  id: string;
  title: string;
  display_title: string;
  status: HbolStatus;
  container: string;
  forwarder: string;
  file_count: number;
  row_count: number;
  created_by_email: string;
  created_at: string;
  updated_at: string;
}

export interface HbolJob {
  id: string;
  title: string;
  display_title: string;
  status: HbolStatus;
  error: string;
  container: string;
  extracted: HbolExtracted;
  skipped: { name: string; reason: string }[];
  files: HbolFile[];
  rows: HbolRow[];
  totals: HbolTotals;
  reading_started_at: string | null;
  reading_finished_at: string | null;
  created_by_email: string;
  updated_by_email: string;
  created_at: string;
  updated_at: string;
}

export function hbolJobsQuery(params: { search?: string; page?: number }) {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.page && params.page > 1) qs.set("page", String(params.page));
  const suffix = qs.toString() ? `?${qs}` : "";
  return queryOptions({
    queryKey: ["hbol", "jobs", params],
    queryFn: () => api<Paginated<HbolJobListItem>>(`/hbol/jobs/${suffix}`),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.results.some((j) => j.status === "reading") ? 5000 : false,
  });
}

export function hbolJobQuery(id: string) {
  return queryOptions({
    queryKey: ["hbol", "job", id],
    queryFn: () => api<HbolJob>(`/hbol/jobs/${id}/`),
    // Keep the page live while the documents are being read.
    refetchInterval: (query) => (query.state.data?.status === "reading" ? 3000 : false),
  });
}

function filesForm(files: File[], form = new FormData()): FormData {
  for (const file of files) form.append("files", file);
  return form;
}

export const hbolApi = {
  /** Upload PDFs, ZIPs or RARs; reading starts right away. */
  create: (title: string, files: File[]) => {
    const form = filesForm(files);
    form.set("title", title);
    return api<HbolJob>("/hbol/jobs/", { method: "POST", body: form });
  },
  addFiles: (id: string, files: File[]) =>
    api<HbolJob>(`/hbol/jobs/${id}/files/`, { method: "POST", body: filesForm(files) }),
  read: (id: string) => api<HbolJob>(`/hbol/jobs/${id}/read/`, { method: "POST" }),
  rename: (id: string, title: string) =>
    api<HbolJob>(`/hbol/jobs/${id}/`, { method: "PATCH", body: { title } }),
  remove: (id: string) => api<void>(`/hbol/jobs/${id}/`, { method: "DELETE" }),
  removeFile: (fileId: string) => api<void>(`/hbol/files/${fileId}/`, { method: "DELETE" }),
  addRow: (id: string, row: RowPatch) =>
    api<HbolRow>(`/hbol/jobs/${id}/rows/`, { method: "POST", body: row }),
  updateRow: (rowId: string, patch: RowPatch) =>
    api<HbolRow>(`/hbol/rows/${rowId}/`, { method: "PATCH", body: patch }),
  deleteRow: (rowId: string) => api<void>(`/hbol/rows/${rowId}/`, { method: "DELETE" }),
  orderRows: (id: string, ids: string[]) =>
    api<HbolJob>(`/hbol/jobs/${id}/rows/order/`, { method: "POST", body: { ids } }),
  exportUrl: (id: string, kind: "csv" | "xlsx") => `${API_URL}/api/hbol/jobs/${id}/export/${kind}/`,
};

/** The row ids with `id` moved one place up or down; unchanged at either end. */
export function moved(ids: string[], id: string, step: -1 | 1): string[] {
  const from = ids.indexOf(id);
  const to = from + step;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** "3 manifest · 7 house bills · 24 invoices" for a job's sorted pages. */
export function describePages(pages: Partial<Record<PageKind, number>> | undefined): string {
  if (!pages) return "";
  const order: [PageKind, string, string][] = [
    ["manifest", "manifest page", "manifest pages"],
    ["house_bl", "house-bill page", "house-bill pages"],
    ["master_bl", "master-bill page", "master-bill pages"],
    ["invoice", "invoice page", "invoice pages"],
    ["other", "other page", "other pages"],
  ];
  return order
    .filter(([kind]) => pages[kind])
    .map(([kind, one, many]) => `${pages[kind]} ${pages[kind] === 1 ? one : many}`)
    .join(" · ");
}

/** "3949.8" for "3949.800"; the export writes numbers the same way. */
export function plainNumber(value: string | null): string {
  if (value === null || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? value : String(n);
}

/** Totals per unit as "4,095.18 KG + 10 LB". */
export function describeTotals(byUnit: Record<string, string>): string {
  const parts = Object.entries(byUnit).map(
    ([unit, value]) =>
      `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ""}`,
  );
  return parts.join(" + ") || "—";
}

/** Archive or PDF, as the upload accepts them. */
export function isAcceptedUpload(name: string): boolean {
  return /\.(pdf|zip|rar)$/i.test(name);
}

/** Consecutive pages of one kind as runs ("Manifest 1-3"), so a 137-page scan stays readable. */
export function pageRuns(file: HbolFile): { kind: PageKind; first: number; last: number }[] {
  const runs: { kind: PageKind; first: number; last: number }[] = [];
  for (const page of file.pages) {
    const last = runs[runs.length - 1];
    if (last && last.kind === page.kind && last.last === page.page - 1) last.last = page.page;
    else runs.push({ kind: page.kind, first: page.page, last: page.page });
  }
  return runs;
}
